import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthCard, AuthCardPage, StepsAside } from "@/components/auth/AuthLayout";
import { PhoneVisual } from "@/components/auth/AuthIllustrations";
import { TwoFactorSetup } from "@/components/settings/TwoFactorSetup";

/**
 * Where people are sent when the company requires two-step verification for their role and they have not set it up yet.
 * Nothing else in the CRM opens until it is on.
 */
export function SetupTwoStepPage() {
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState(0);

  const leave = (
    <button
      type="button"
      onClick={async () => {
        await logout();
        navigate("/login", { replace: true });
      }}
      className="text-sm font-medium text-primary hover:underline"
    >
      Log out
    </button>
  );

  return (
    <AuthCardPage
      topRight={leave}
      aside={
        <StepsAside
          eyebrow="Account security"
          title="A second step keeps your account yours."
          text="After your password, a 6-digit code from an app on your phone confirms it is you. A stolen password alone is no longer enough."
          steps={["Scan the QR code with your authenticator app", "Enter the 6-digit code it shows", "Save your backup codes"]}
          visual={(step) => <PhoneVisual step={step} />}
          current={stage}
        />
      }
    >
      <AuthCard
        title="Set up two-step verification"
        subtitle="Your company requires a code from an authenticator app when you sign in. It takes about a minute."
        className="max-w-none"
      >
        <TwoFactorSetup
          onStageChange={setStage}
          onDone={async () => {
            await refreshUser();
            navigate("/dashboard", { replace: true });
          }}
        />
      </AuthCard>
    </AuthCardPage>
  );
}
